/**
 * `useMe` — loads the authenticated user's profile and organizations from the
 * API (`GET /api/v1/me`). It activates once the session is discovered
 * (server-side, HttpOnly cookie), and refetches when the user changes.
 */

import { useQuery } from "@tanstack/react-query";

import { useAppAuth } from "./use-auth";

import { api, ApiError } from "../lib/api-client";
import type { MeResponse } from "../lib/auth";

/** Don't retry auth failures — the session is dead until re-authentication. */
export function noRetryOnAuthErrors(failureCount: number, error: unknown) {
  if (
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return false;
  }
  return failureCount < 2;
}

export function useMe() {
  const { user } = useAppAuth();

  return useQuery<MeResponse>({
    queryKey: ["me", user?.id],
    enabled: !!user,
    staleTime: 30_000,
    retry: noRetryOnAuthErrors,
    queryFn: ({ signal }) => api.me(signal),
  });
}
